import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { APPOINTMENT_STATUS, cardColors, formatCurrency, formatDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "@/components/ui/hover-card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, ChevronRight, Clock, CalendarDays, CalendarRange, LogIn, Stethoscope,
  Phone, Mail, Pencil, UserX, IndianRupee, Users2, Printer, Plus, Ban,
} from "lucide-react";
import { AvailabilityDialog, unavailableRanges, dayHours } from "@/components/crm/Availability";

const SLOT_MINUTES = 15;
const MIN_HOUR = 7;
const MAX_HOUR = 22;
const FALLBACK_START = 9;
const FALLBACK_END = 18;
const PX_PER_MIN = 3.2;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function StatusBadge({ status }) {
  const s = APPOINTMENT_STATUS.find((x) => x.value === status) || APPOINTMENT_STATUS[0];
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.color}`}>{s.label}</span>;
}

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

// Cancelled and no-show are struck through wherever they appear, so a glance at
// the day never counts a visit that did not happen.
const isDeadAppt = (a) => a.status === "cancelled" || a.status === "no_show";

// Blocked time — leave, lunch, a machine being serviced. A block with no
// doctor_id covers the whole clinic.
function blocksFor(blocks, day, doctorId) {
  return (blocks || []).filter((b) => {
    if ((b.from_at || "").slice(0, 10) !== day) return false;
    return !b.doctor_id || b.doctor_id === doctorId;
  });
}

// Where a block sits on a time grid, in minutes from the grid's first hour.
function blockBand(b, startHour) {
  const f = new Date(b.from_at);
  const t = new Date(b.to_at);
  const from = f.getHours() * 60 + f.getMinutes() - startHour * 60;
  const to = t.getHours() * 60 + t.getMinutes() - startHour * 60;
  return { from, to: Math.max(to, from + 10) };
}

/* Category colours.
 *
 * Practo colours the calendar by APPOINTMENT CATEGORY (LHR, GFC, Fractional…)
 * and shows status separately. Colouring by status instead — which is what we
 * did — answers "who is waiting" but not "what is this clinic doing today",
 * and the second question is the one a manager scanning a week is asking.
 *
 * So: category drives the colour when the appointment has one, status is the
 * fallback, and status stays legible everywhere it matters (the badge in the
 * rail and the hover card, and the strike-through on a cancellation). */
const CATEGORY_PALETTE = [
  { bar: "#1B3A5C", head: "#E3EAF2" },   // navy
  { bar: "#C49A6C", head: "#F5EADB" },   // gold
  { bar: "#2E7D6F", head: "#DFEFEA" },   // teal
  { bar: "#8E5B9F", head: "#EFE3F3" },   // violet
  { bar: "#C25E4D", head: "#F8E4E0" },   // terracotta
  { bar: "#4A6FA5", head: "#E4EBF5" },   // steel
  { bar: "#7A8B3C", head: "#EDF1DE" },   // olive
  { bar: "#B0763C", head: "#F6E9DA" },   // amber
];

function categoryColor(category, categories) {
  if (!category) return null;
  const i = categories.indexOf(category);
  return CATEGORY_PALETTE[(i < 0 ? category.length : i) % CATEGORY_PALETTE.length];
}

// The colour an appointment card is drawn in. Cancelled/no-show always read as
// dead regardless of category — a struck-through row in full category colour
// still looks like a live booking at a glance.
function apptColor(a, categories) {
  if (isDeadAppt(a)) return cardColors(a.status);
  return categoryColor(a.category, categories) || cardColors(a.status);
}

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === "doctor";
  // Check-in / Start / Resume are clinic-desk actions — hidden from telecallers,
  // same as on the Appointments page (see CLINIC_ONLY_STATUSES in constants).
  const canDoClinicActions = user?.role !== "telecaller";
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availability, setAvailability] = useState({});
  const [availEditing, setAvailEditing] = useState(null);
  const [calDate, setCalDate] = useState(todayISO());
  const [calMode, setCalMode] = useState(isDoctor ? "month" : "day");
  const [showCancelled, setShowCancelled] = useState(true);
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [blocking, setBlocking] = useState(false);

  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  // Clinic opening hours bound the day grid. Without them the grid is derived
  // purely from doctor availability and whatever is booked, so a clinic that
  // opens at 07:00 but has nothing booked before 10:00 shows no early slots.
  const [clinicHours, setClinicHours] = useState(null);

  useEffect(() => {
    api.get("/master-data", { params: { field: "appointment_category" } })
      .then((r) => setCategories((r.data.appointment_category || []).map((c) => c.value)))
      .catch(() => {});
    api.get("/branches").then((r) => {
      const first = (r.data || [])[0];
      if (!first) return;
      api.get(`/clinic-settings/${first.id}`)
        .then((cs) => setClinicHours({
          start: cs.data?.day_start_hour ?? null,
          end: cs.data?.day_end_hour ?? null,
        }))
        .catch(() => {});
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/appointments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/calendar-blocks").then((r) => setBlocks(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeBlock = async (id) => {
    try {
      await api.delete(`/calendar-blocks/${id}`);
      toast.success("Block removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (isDoctor) {
      setDoctors(user ? [user] : []);
    } else {
      const p = { role: "doctor" };
      api.get("/users", { params: p }).then((r) => setDoctors(r.data)).catch(() => {});
    }
  }, [isDoctor, user]);

  useEffect(() => {
    doctors.forEach((d) => {
      api.get(`/users/${d.id}/availability`)
        .then((r) => setAvailability((m) => ({ ...m, [d.id]: r.data })))
        .catch(() => {});
    });
  }, [doctors]);

  const updateStatus = async (id, status, extra = {}) => {
    // Check-in is where reception corrects details, assigns the doctor and
    // records vitals — never a bare status flip.
    if (status === "checked_in") { navigate(`/checkin/${id}`); return; }
    try {
      await api.put(`/appointments/${id}`, { status, ...extra });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startConsultation = (id) => navigate(`/consultation/${id}`);

  // One filter pipeline for every view, so the doctor rail, the grid and the
  // schedule rail can never disagree about what is on the calendar.
  //
  // Two stages on purpose. The doctor rail counts the cancelled-filtered set but
  // NOT the doctor-filtered one: its whole job is to let you switch doctors, so
  // narrowing it by the current doctor would show every other doctor as 0.
  const shownStatuses = useMemo(
    () => (showCancelled ? items : items.filter((a) => a.status !== "cancelled")),
    [items, showCancelled],
  );
  const visible = useMemo(
    () => (doctorFilter === "all"
      ? shownStatuses
      : shownStatuses.filter((a) => a.doctor_id === doctorFilter)),
    [shownStatuses, doctorFilter],
  );

  const shownDoctors = useMemo(
    () => (doctorFilter === "all" ? doctors : doctors.filter((d) => d.id === doctorFilter)),
    [doctors, doctorFilter],
  );

  // Everything the hover card needs, bundled once rather than threaded as five
  // separate props through both views.
  const peekProps = { doctors, canDoClinicActions, onStartConsultation: startConsultation, updateStatus };

  // Legend the categories that are actually in use, not the whole master list —
  // a key to ten colours when the clinic only runs three is noise. Derived from
  // everything loaded rather than the exact visible window: a legend is a key,
  // and a key with one spare entry is harmless where a missing one is not.
  const usedCategories = useMemo(
    () => [...new Set(visible.map((a) => a.category).filter(Boolean))],
    [visible],
  );

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-navy/10 bg-white p-0.5">
          {[["day", "Day", Clock], ["week", "Week", CalendarRange], ["month", "Month", CalendarDays]].map(
            ([mode, label, Icon]) => (
              <button key={mode} onClick={() => setCalMode(mode)} data-testid={`cal-mode-${mode}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  calMode === mode ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={showCancelled} onCheckedChange={setShowCancelled} data-testid="show-cancelled-toggle" />
            Show cancelled
          </label>
          {canDoClinicActions && (
            <Button onClick={() => navigate("/appointments/book?walkin=1")}
                    className="bg-gold text-navy hover:bg-gold/90" data-testid="add-walkin-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add Walk-in
            </Button>
          )}
          {!isDoctor && (
            <Button variant="outline" onClick={() => setBlocking(true)} data-testid="block-calendar-btn">
              <Ban className="mr-1.5 h-4 w-4" /> Block
            </Button>
          )}
          <Button variant="outline" size="icon" title="Print this view" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
          </Button>
          {isDoctor && user && (
            <Button variant="outline" onClick={() => setAvailEditing({ id: user.id, name: user.name })}>
              <Clock className="mr-2 h-4 w-4" /> My Availability
            </Button>
          )}
        </div>
      </div>

      {usedCategories.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-wider">Categories</span>
          {usedCategories.map((c) => {
            const col = categoryColor(c, categories);
            return (
              <span key={c} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: col.bar }} />
                {c}
              </span>
            );
          })}
        </div>
      )}

      {/* Column template has to match the child count — a doctor has no doctor
          rail, and leaving three templates put the grid in the 190px column. */}
      <div className={`grid gap-5 ${isDoctor
        ? "xl:grid-cols-[minmax(0,1fr)_310px]"
        : "xl:grid-cols-[190px_minmax(0,1fr)_310px]"}`}>
        {!isDoctor && (
          <DoctorRail
            doctors={doctors}
            appointments={shownStatuses}
            mode={calMode}
            date={calDate}
            selected={doctorFilter}
            onSelect={setDoctorFilter}
          />
        )}

        <div className="min-w-0">
          {calMode === "month" && (
            <MonthCalendar
              date={calDate}
              setDate={setCalDate}
              appointments={visible}
              onSelectDay={setCalDate}
              categories={categories}
            />
          )}

          {calMode === "week" && (
            <WeekView
              date={calDate}
              setDate={setCalDate}
              appointments={visible}
              peekProps={peekProps}
              blocks={blocks}
              categories={categories}
            />
          )}

          {calMode === "day" && (
            <DayView
              date={calDate}
              setDate={setCalDate}
              appointments={visible}
              doctors={shownDoctors}
              availability={availability}
              isDoctor={isDoctor}
              canEditAvailability={!isDoctor}
              onEditAvailability={(d) => setAvailEditing({ id: d.id, name: d.name })}
              peekProps={peekProps}
              blocks={blocks}
              onRemoveBlock={removeBlock}
              categories={categories}
              clinicHours={clinicHours}
            />
          )}
        </div>

        <ScheduleRail
          date={calDate}
          appointments={visible}
          doctors={doctors}
          peekProps={peekProps}
        />
      </div>

      <BlockCalendarDialog
        open={blocking}
        date={calDate}
        doctors={doctors}
        onClose={() => setBlocking(false)}
      />

      <AvailabilityDialog
        open={!!availEditing}
        userId={availEditing?.id}
        userName={availEditing?.name}
        onClose={() => setAvailEditing(null)}
        onSaved={() => {
          doctors.forEach((d) => {
            api.get(`/users/${d.id}/availability`)
              .then((r) => setAvailability((m) => ({ ...m, [d.id]: r.data })))
              .catch(() => {});
          });
        }}
      />
    </div>
  );
}

/* ───────────────────────── Month calendar ───────────────────────── */

function MonthCalendar({ date, setDate, appointments, onSelectDay, categories }) {
  const viewYear = parseInt(date.slice(0, 4), 10);
  const viewMonth = parseInt(date.slice(5, 7), 10) - 1;

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
  };

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const weeks = [];
  let week = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

  // Names, not just a count — the month view is how staff spot "is Mrs Shah in
  // this week", and a bare "5 appts" cannot answer that.
  const apptsByDay = {};
  appointments.forEach((a) => {
    const aDate = (a.scheduled_at || "").slice(0, 10);
    if (!aDate) return;
    (apptsByDay[aDate] = apptsByDay[aDate] || []).push(a);
  });
  Object.values(apptsByDay).forEach((list) =>
    list.sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")));

  const todayStr = todayISO();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div>
      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-navy">{monthLabel}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="h-8 text-xs" onClick={() => setDate(todayISO())}>Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 text-center text-xs font-semibold text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        {weeks.map((wk, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {wk.map((day, di) => {
              if (!day) return <div key={di} className="h-28 border-t border-navy/5" />;
              const dayStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayList = apptsByDay[dayStr] || [];
              const isToday = dayStr === todayStr;
              const isSelected = dayStr === date;
              return (
                <button key={di} onClick={() => onSelectDay(dayStr)}
                  className={`h-28 overflow-hidden border-t border-navy/5 p-1.5 text-left align-top transition-colors hover:bg-nude/30 ${isSelected ? "bg-gold/10 ring-1 ring-inset ring-gold/30" : ""}`}>
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-navy text-white" : "text-ink"}`}>{day}</span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayList.slice(0, 3).map((a) => {
                      const c = apptColor(a, categories);
                      return (
                        <div key={a.id} className="truncate rounded px-1 py-px text-[10px] leading-tight text-ink"
                          style={{ backgroundColor: c.head, borderLeft: `2px solid ${c.bar}` }}>
                          <span className={isDeadAppt(a) ? "line-through opacity-60" : ""}>{a.patient_name}</span>
                        </div>
                      );
                    })}
                    {dayList.length > 3 && (
                      <p className="px-1 text-[10px] font-semibold text-muted-foreground">+{dayList.length - 3} more</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────── Block calendar dialog ───────────────────── */

// Leave, lunch, a machine being serviced. Weekly availability cannot say any of
// it: availability repeats every week, and "Dr Rao is away next Thursday" is a
// one-off fact.
function BlockCalendarDialog({ open, date, doctors, onClose }) {
  const [form, setForm] = useState({ doctor_id: "all", from: "09:00", to: "18:00", day: date, reason: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ doctor_id: "all", from: "09:00", to: "18:00", day: date, reason: "" });
  }, [open, date]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/calendar-blocks", {
        doctor_id: form.doctor_id === "all" ? null : form.doctor_id,
        from_at: new Date(`${form.day}T${form.from}:00`).toISOString(),
        to_at: new Date(`${form.day}T${form.to}:00`).toISOString(),
        reason: form.reason,
      });
      toast.success("Calendar blocked");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Block calendar</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Who</label>
            <Select value={form.doctor_id} onValueChange={(v) => setForm((f) => ({ ...f, doctor_id: v }))}>
              <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole clinic</SelectItem>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Date</label>
              <Input type="date" value={form.day} onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <Input type="time" value={form.from} onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <Input type="time" value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Reason</label>
            <Input value={form.reason} placeholder="Leave / lunch / machine servicing"
                   onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          <p className="text-xs text-muted-foreground">
            A block is a warning, not a lock — reception can still book into it when they need to.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Saving…" : "Block"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Week view ───────────────────────── */

// Seven day-columns on one time grid — Practo's default view, and the one
// reception actually plans from ("is Thursday full?"). Reuses the same overlap
// layout as the day view, applied per day rather than per doctor.
function WeekView({ date, setDate, appointments, peekProps, blocks, categories }) {
  const days = useMemo(() => {
    const d = new Date(date + "T00:00:00");
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay());          // week starts Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(start);
      x.setDate(start.getDate() + i);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    });
  }, [date]);

  const weekAppts = useMemo(
    () => appointments.filter((a) => days.includes((a.scheduled_at || "").slice(0, 10))),
    [appointments, days],
  );

  // Bound the grid to what is actually booked, so an empty week is not 15 hours
  // of blank rows and a late clinic is not clipped.
  const { startHour, endHour } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    weekAppts.forEach((a) => {
      const s = new Date(a.scheduled_at);
      const m = s.getHours() * 60 + s.getMinutes();
      lo = Math.min(lo, m);
      hi = Math.max(hi, m + (a.duration_minutes || 60));
    });
    if (lo === Infinity) { lo = FALLBACK_START * 60; hi = FALLBACK_END * 60; }
    const sh = Math.max(MIN_HOUR, Math.floor(lo / 60));
    let eh = Math.min(MAX_HOUR, Math.ceil(hi / 60));
    if (eh <= sh) eh = sh + 1;
    return { startHour: sh, endHour: eh };
  }, [weekAppts]);

  const hours = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);
  const totalMinutes = (endHour - startHour) * 60;
  const pxPerMin = 1.9;                      // tighter than the day view: 7 columns
  const gridHeight = totalMinutes * pxPerMin;

  const shiftWeek = (delta) => {
    const d = new Date(days[0] + "T00:00:00");
    d.setDate(d.getDate() + delta * 7);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const label = `${new Date(days[0] + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${
    new Date(days[6] + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  const todayStr = todayISO();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Week</p>
          <h2 className="font-display text-2xl font-semibold text-navy">{label}</h2>
          <p className="text-sm text-muted-foreground">{weekAppts.length} appointment{weekAppts.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftWeek(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9" onClick={() => setDate(todayISO())}>Today</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftWeek(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex min-w-[700px]">
          <div className="w-14 flex-shrink-0 border-r border-navy/10">
            <div className="h-12 border-b border-navy/10" />
            <div className="relative" style={{ height: gridHeight }}>
              {hours.map((h, i) => (
                <div key={h} className="absolute left-0 right-1 text-right text-[10px] text-muted-foreground"
                     style={{ top: i * 60 * pxPerMin - 5 }}>
                  {`${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "am" : "pm"}`}
                </div>
              ))}
            </div>
          </div>

          {days.map((day) => {
            const dayAppts = weekAppts.filter((a) => (a.scheduled_at || "").slice(0, 10) === day);
            const isToday = day === todayStr;
            const d = new Date(day + "T00:00:00");
            return (
              <div key={day} className="min-w-0 flex-1 border-r border-navy/10 last:border-r-0">
                <button onClick={() => setDate(day)}
                  className={`flex h-12 w-full flex-col items-center justify-center border-b border-navy/10 hover:bg-nude/30 ${
                    day === date ? "bg-gold/10" : ""}`}>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {d.toLocaleDateString(undefined, { weekday: "short" })}
                  </span>
                  <span className={`text-sm font-semibold ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white" : "text-navy"}`}>
                    {d.getDate()}
                  </span>
                </button>
                <div className="relative" style={{ height: gridHeight }}>
                  {hours.map((h, i) => (
                    <div key={h} className="absolute left-0 right-0 border-b border-navy/5"
                         style={{ top: i * 60 * pxPerMin, height: 60 * pxPerMin }} />
                  ))}
                  {/* Clinic-wide blocks only in the week view — a per-doctor
                      block has no column of its own here. */}
                  {blocksFor(blocks, day, null).filter((b) => !b.doctor_id).map((b) => {
                    const band = blockBand(b, startHour);
                    return (
                      <div key={b.id} className="pointer-events-none absolute left-0 right-0 z-0 bg-red-50/70"
                        style={{ top: band.from * pxPerMin, height: (band.to - band.from) * pxPerMin,
                          backgroundImage: "repeating-linear-gradient(45deg, rgba(220,38,38,0.10) 0, rgba(220,38,38,0.10) 5px, transparent 5px, transparent 10px)" }}
                        title={b.reason || "Blocked"} />
                    );
                  })}
                  {layoutColumnAppts(dayAppts).map(({ appt: a, lane, lanes }) => {
                    const s = new Date(a.scheduled_at);
                    const fromStart = (s.getHours() - startHour) * 60 + s.getMinutes();
                    if (fromStart < 0 || fromStart > totalMinutes) return null;
                    const c = apptColor(a, categories);
                    const w = 100 / lanes;
                    return (
                      <ApptHover key={a.id} appointment={a} peek={peekProps}
                        className="absolute z-10 overflow-hidden rounded px-1 text-left ring-1 ring-black/5 hover:z-20 hover:shadow-md"
                        style={{
                          top: fromStart * pxPerMin,
                          height: Math.max((a.duration_minutes || 60) * pxPerMin - 1, 16),
                          left: `${lane * w}%`, width: `${w - 1}%`,
                          backgroundColor: c.head, borderLeft: `2px solid ${c.bar}`,
                        }}>
                        <p className="truncate text-[10px] font-semibold leading-tight text-ink">
                          <span className={isDeadAppt(a) ? "line-through opacity-60" : ""}>{a.patient_name}</span>
                        </p>
                        <p className="truncate text-[9px] leading-tight text-ink/55">{fmtTime(a.scheduled_at)}</p>
                      </ApptHover>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Doctor rail ───────────────────────── */

// Counts are for the PERIOD ON SCREEN, not all time — a doctor's number has to
// agree with what the grid next to it is showing.
function DoctorRail({ doctors, appointments, mode, date, selected, onSelect }) {
  const inPeriod = useMemo(() => {
    const prefix = mode === "month" ? date.slice(0, 7) : date;
    return appointments.filter((a) => (a.scheduled_at || "").startsWith(prefix));
  }, [appointments, mode, date]);

  const countFor = (id) => inPeriod.filter((a) => a.doctor_id === id).length;

  const Row = ({ id, name, count, dot }) => (
    <button onClick={() => onSelect(id)}
      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
        selected === id ? "bg-navy text-white" : "text-ink hover:bg-nude/40"}`}>
      <span className="flex min-w-0 items-center gap-2">
        {dot && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: dot }} />}
        <span className="truncate">{name}</span>
      </span>
      <span className={`shrink-0 text-xs font-semibold ${selected === id ? "text-white/80" : "text-muted-foreground"}`}>{count}</span>
    </button>
  );

  return (
    <div className="h-fit overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
      <p className="border-b border-navy/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Users2 className="mr-1.5 inline h-3.5 w-3.5" /> Doctors
      </p>
      <Row id="all" name="All doctors" count={inPeriod.length} />
      {doctors.map((d) => (
        <Row key={d.id} id={d.id} name={d.name} count={countFor(d.id)} dot="#C49A6C" />
      ))}
    </div>
  );
}

/* ───────────────────────── Schedule rail ───────────────────────── */

// The front desk's board for the day on screen. Practo calls this Today's
// Schedule; it follows the selected day rather than being pinned to today,
// because reception's next question after "who's in today" is "who's in
// tomorrow".
// Which statuses each counter tile stands for. The tiles double as filters —
// "3 waiting" is only half an answer; reception's next question is always
// *which* three.
const RAIL_BUCKETS = {
  total: null,                                        // null = no status filter
  waiting: ["checked_in"],
  engaged: ["in_consultation"],
  done: ["checked_out", "completed", "visited"],
};

function ScheduleRail({ date, appointments, doctors, peekProps }) {
  const [bucket, setBucket] = useState("total");

  const dayAppts = useMemo(() => appointments
    .filter((a) => (a.scheduled_at || "").slice(0, 10) === date)
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
  [appointments, date]);

  const counts = useMemo(() => ({
    total: dayAppts.filter((a) => a.status !== "cancelled").length,
    waiting: dayAppts.filter((a) => RAIL_BUCKETS.waiting.includes(a.status)).length,
    engaged: dayAppts.filter((a) => RAIL_BUCKETS.engaged.includes(a.status)).length,
    done: dayAppts.filter((a) => RAIL_BUCKETS.done.includes(a.status)).length,
  }), [dayAppts]);

  const listed = useMemo(() => {
    const allowed = RAIL_BUCKETS[bucket];
    return allowed ? dayAppts.filter((a) => allowed.includes(a.status)) : dayAppts;
  }, [dayAppts, bucket]);

  const docName = (id) => doctors.find((d) => d.id === id)?.name || "Unassigned";
  const isToday = date === todayISO();
  const label = isToday ? "Today's Schedule"
    : `${new Date(date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })} Schedule`;

  const Tile = ({ id, label: l, value, tone }) => (
    <button onClick={() => setBucket(id)} data-testid={`rail-tile-${id}`}
      className={`rounded-lg py-1 text-center transition-colors ${
        bucket === id ? "bg-white shadow-sm ring-1 ring-navy/15" : "hover:bg-white/60"}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
      <p className={`mt-0.5 inline-flex min-w-[2rem] justify-center rounded px-1.5 py-0.5 text-sm font-bold ${tone}`}>{value}</p>
    </button>
  );

  return (
    <div className="h-fit rounded-xl border border-navy/10 bg-white shadow-sm">
      <p className="border-b border-navy/10 px-4 py-2.5 font-display text-sm font-semibold text-navy">{label}</p>

      <div className="grid grid-cols-4 gap-1 border-b border-navy/10 bg-cream/40 px-2 py-2">
        <Tile id="total" label="Total" value={counts.total} tone="bg-navy/10 text-navy" />
        <Tile id="waiting" label="Waiting" value={counts.waiting} tone="bg-amber-100 text-amber-800" />
        <Tile id="engaged" label="Engaged" value={counts.engaged} tone="bg-indigo-100 text-indigo-800" />
        <Tile id="done" label="Done" value={counts.done} tone="bg-green-100 text-green-800" />
      </div>

      <div className="max-h-[32rem] divide-y divide-navy/5 overflow-y-auto">
        {listed.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {bucket === "total" ? "Nothing booked." : `No one ${bucket === "done" ? "done" : bucket}.`}
          </p>
        ) : listed.map((a) => {
          const dead = isDeadAppt(a);
          return (
            <ApptHover key={a.id} appointment={a} peek={peekProps}
              className="flex w-full items-start gap-2 px-4 py-2.5 text-left hover:bg-nude/30"
              data-testid="schedule-rail-item">
              <span className="w-16 shrink-0 pt-0.5 text-[11px] font-semibold text-muted-foreground">{fmtTime(a.scheduled_at)}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className={`truncate text-sm font-semibold text-ink ${dead ? "line-through opacity-60" : ""}`}>{a.patient_name}</span>
                  {a.is_first_visit && (
                    <span title="First visit" className="shrink-0 rounded bg-green-600 px-1 text-[9px] font-bold leading-4 text-white">N</span>
                  )}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{a.service || "Visit reason not specified"}</span>
                <span className="block truncate text-xs text-muted-foreground">{docName(a.doctor_id)}</span>
              </span>
              <StatusBadge status={a.status} />
            </ApptHover>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── Appointment popover ───────────────────────── */

// Hovering an appointment shows this card — the same interaction Practo uses,
// so nobody has to click just to find out who is at 3pm and what they owe.
// Clicking an appointment used to do nothing at all: the state was set and
// rendered nowhere.
//
// Patients are cached across hovers. Sweeping the mouse down a full day column
// would otherwise fire a request per appointment, most of them for patients
// already on screen.
const patientCache = new Map();

function ApptHover({ appointment, peek, className, children, ...rest }) {
  return (
    <HoverCard openDelay={180} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button className={className} {...rest}>{children}</button>
      </HoverCardTrigger>
      <HoverCardContent align="start" side="right" className="w-80 p-4" data-testid="appointment-peek">
        <AppointmentPeekCard appointment={appointment} {...peek} />
      </HoverCardContent>
    </HoverCard>
  );
}

function AppointmentPeekCard({ appointment: a, doctors, canDoClinicActions, onStartConsultation, updateStatus }) {
  const navigate = useNavigate();
  const [patient, setPatient] = useState(() => patientCache.get(a?.patient_id) || null);

  useEffect(() => {
    if (!a?.patient_id || patientCache.has(a.patient_id)) return;
    api.get(`/patients/${a.patient_id}`)
      .then((r) => { patientCache.set(a.patient_id, r.data.patient); setPatient(r.data.patient); })
      .catch(() => {});
  }, [a?.patient_id]);

  if (!a) return null;
  const money = patient?.money || {};
  const doctor = doctors.find((d) => d.id === a.doctor_id);
  const dead = isDeadAppt(a);

  return (
    <div className="space-y-3 text-left">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy/10 font-display text-lg font-semibold text-navy">
            {a.patient_name?.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className={`truncate font-display text-lg font-semibold text-navy ${dead ? "line-through opacity-60" : ""}`}>{a.patient_name}</h3>
              {a.is_first_visit && (
                <span title="First visit" className="rounded bg-green-600 px-1 text-[9px] font-bold leading-4 text-white">N</span>
              )}
            </div>
            {patient?.patient_no && <p className="font-mono text-xs text-muted-foreground">{patient.patient_no}</p>}
            <p className="text-xs text-muted-foreground">
              {[patient?.gender, patient?.age ? `${patient.age} years` : null].filter(Boolean).join(" · ") || "—"}
            </p>
            {money.outstanding > 0 && (
              <p className="mt-1 text-sm font-semibold text-red-600" data-testid="peek-balance">
                {formatCurrency(money.outstanding)} due
              </p>
            )}
          </div>
          <StatusBadge status={a.status} />
        </div>

        <div className="space-y-1.5 border-t border-navy/10 pt-3 text-sm">
          {a.mobile && <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> {maskPhone(a.mobile)}</p>}
          {patient?.email && <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {patient.email}</p>}
        </div>

        <div className="rounded-lg bg-cream/60 p-3 text-sm">
          <p className="font-medium text-ink">
            {a.service || "Consultation"} with {doctor?.name || "unassigned doctor"}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDateTime(a.scheduled_at)} · {a.duration_minutes || 60} mins
          </p>
          {a.status_note && <p className="mt-1.5 text-xs text-muted-foreground">Note: {a.status_note}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          {canDoClinicActions && ["scheduled", "confirmed", "ringing"].includes(a.status) && (
            <Button size="sm" onClick={() => updateStatus(a.id, "checked_in")} className="bg-navy text-white hover:bg-navy-hover">
              <LogIn className="mr-1.5 h-3.5 w-3.5" /> Check In
            </Button>
          )}
          {canDoClinicActions && a.status === "checked_in" && (
            <Button size="sm" onClick={() => onStartConsultation(a.id)} className="bg-navy text-white hover:bg-navy-hover">
              <Stethoscope className="mr-1.5 h-3.5 w-3.5" /> Start
            </Button>
          )}
          {canDoClinicActions && a.status === "in_consultation" && (
            <Button size="sm" variant="outline" onClick={() => onStartConsultation(a.id)}>
              <Stethoscope className="mr-1.5 h-3.5 w-3.5" /> Resume
            </Button>
          )}
          {canDoClinicActions && ["checked_out", "completed"].includes(a.status) && (
            <Button size="sm" onClick={() => navigate(`/checkout/${a.id}`)} className="bg-gold text-navy hover:bg-gold/90">
              <IndianRupee className="mr-1.5 h-3.5 w-3.5" /> Collect Payment
            </Button>
          )}
          {!dead && a.status !== "visited" && (
            <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "no_show", { status_note: "Marked from calendar" })}
              className="text-muted-foreground">
              <UserX className="mr-1.5 h-3.5 w-3.5" /> No Show
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate("/appointments")}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
          </Button>
          {a.patient_id && (
            <Button size="sm" variant="ghost" onClick={() => navigate(`/patients/${a.patient_id}`)} className="text-navy">
              Open chart
            </Button>
          )}
        </div>
    </div>
  );
}

/* ───────────────────────── Day view (admin: multi-column, doctor: single) ───────────────────────── */

function layoutColumnAppts(appts) {
  const items = appts
    .map((a) => {
      const s = new Date(a.scheduled_at);
      const startMin = s.getHours() * 60 + s.getMinutes();
      return { appt: a, start: startMin, end: startMin + (a.duration_minutes || 60) };
    })
    .sort((x, y) => x.start - y.start || x.end - y.end);
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = Math.max(...cluster.map((c) => c.lane + 1), 1);
    cluster.forEach((c) => (c.lanes = lanes));
    cluster = [];
  };
  const laneEnds = [];
  for (const it of items) {
    if (it.start >= clusterEnd && cluster.length) { flush(); laneEnds.length = 0; }
    let lane = laneEnds.findIndex((e) => e <= it.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); }
    else laneEnds[lane] = it.end;
    it.lane = lane;
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  if (cluster.length) flush();
  return items;
}

function DayView({ date, setDate, appointments, doctors, availability, isDoctor, canEditAvailability, onEditAvailability, peekProps, blocks, onRemoveBlock, categories, clinicHours }) {
  const dayAppts = useMemo(() => appointments.filter((a) => (a.scheduled_at || "").slice(0, 10) === date), [appointments, date]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const { DAY_START_HOUR, DAY_END_HOUR } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    doctors.forEach((doc) => {
      const dh = dayHours(availability?.[doc.id], date);
      if (dh) { lo = Math.min(lo, dh.start); hi = Math.max(hi, dh.end); }
    });
    if (lo === Infinity) { lo = FALLBACK_START * 60; hi = FALLBACK_END * 60; }
    // Clinic hours widen the window; they never narrow it, because a booking
    // outside opening hours still has to be visible.
    if (clinicHours?.start != null) lo = Math.min(lo, clinicHours.start * 60);
    if (clinicHours?.end != null) hi = Math.max(hi, clinicHours.end * 60);
    dayAppts.forEach((a) => {
      const s = new Date(a.scheduled_at);
      const startMin = s.getHours() * 60 + s.getMinutes();
      lo = Math.min(lo, startMin);
      hi = Math.max(hi, startMin + (a.duration_minutes || 60));
    });
    let startH = Math.max(MIN_HOUR, Math.floor(lo / 60));
    let endH = Math.min(MAX_HOUR, Math.ceil(hi / 60));
    if (endH <= startH) endH = startH + 1;
    return { DAY_START_HOUR: startH, DAY_END_HOUR: endH };
  }, [doctors, availability, date, dayAppts, clinicHours]);

  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const gridHeight = totalMinutes * PX_PER_MIN;

  const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
  const fmtRange = (start, mins) => {
    const s = new Date(start);
    const e = new Date(s.getTime() + mins * 60000);
    const t = (d) => `${d.getHours() % 12 === 0 ? 12 : d.getHours() % 12}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() < 12 ? "AM" : "PM"}`;
    return `${t(s)} – ${t(e)}`;
  };

  const niceDate = new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Schedule</p>
          <h2 className="font-display text-2xl font-semibold text-navy">{niceDate}</h2>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" /> {dayAppts.length} appointment{dayAppts.length !== 1 ? "s" : ""} today
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9" onClick={() => setDate(todayISO())}>Today</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} className="h-9 w-40 bg-white" />
        </div>
      </div>

      {doctors.length === 0 ? (
        <div className="rounded-xl border border-navy/10 bg-white p-12 text-center text-muted-foreground">No staff to display.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white shadow-sm">
          <div className="flex min-w-max">
            <div className="w-16 flex-shrink-0 border-r border-navy/10">
              <div className="h-12 border-b border-navy/10" />
              <div className="relative" style={{ height: gridHeight }}>
                {hours.map((h, i) => (
                  <div key={h} className="absolute left-0 right-1 text-right text-[11px] text-muted-foreground" style={{ top: i * 60 * PX_PER_MIN - 6 }}>{fmtHour(h)}</div>
                ))}
              </div>
            </div>
            {doctors.map((doc) => {
              const colAppts = dayAppts.filter((a) => a.doctor_id === doc.id);
              const offRanges = unavailableRanges(availability?.[doc.id], date, DAY_START_HOUR, DAY_END_HOUR);
              return (
                <div key={doc.id} className="w-64 flex-shrink-0 border-r border-navy/10 last:border-r-0">
                  <div className="flex h-12 items-center justify-between border-b border-navy/10 px-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-gold" />
                      <span className="text-sm font-semibold text-navy">{doc.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{(doc.role || "").replace("_", " ")}</span>
                      {canEditAvailability && (
                        <button onClick={() => onEditAvailability(doc)} className="rounded p-0.5 text-muted-foreground hover:bg-nude hover:text-navy">
                          <Clock className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="relative" style={{ height: gridHeight }}>
                    {offRanges.map((r, idx) => (
                      <div key={`off-${idx}`} className="pointer-events-none absolute left-0 right-0 bg-navy/[0.06]"
                        style={{ top: (r.from - DAY_START_HOUR * 60) * PX_PER_MIN, height: (r.to - r.from) * PX_PER_MIN,
                          backgroundImage: "repeating-linear-gradient(45deg, rgba(27,58,92,0.05) 0, rgba(27,58,92,0.05) 6px, transparent 6px, transparent 12px)" }} />
                    ))}
                    {/* Blocked time. Drawn UNDER the appointments (z-0) on
                        purpose: a block is a warning, and reception can still
                        book into it, so a booking must never be hidden by one. */}
                    {blocksFor(blocks, date, doc.id).map((b) => {
                      const band = blockBand(b, DAY_START_HOUR);
                      return (
                        <div key={b.id} className="group absolute left-0 right-0 z-0 border-y border-red-300/60 bg-red-50/70"
                          style={{ top: band.from * PX_PER_MIN, height: (band.to - band.from) * PX_PER_MIN,
                            backgroundImage: "repeating-linear-gradient(45deg, rgba(220,38,38,0.10) 0, rgba(220,38,38,0.10) 5px, transparent 5px, transparent 10px)" }}
                          title={b.reason || "Blocked"}>
                          <div className="flex items-start justify-between px-1.5 pt-0.5">
                            <span className="truncate text-[10px] font-semibold text-red-800">
                              {b.reason || "Blocked"}
                            </span>
                            {onRemoveBlock && (
                              <button onClick={() => onRemoveBlock(b.id)} title="Remove block"
                                className="hidden shrink-0 text-[10px] font-bold text-red-700 hover:text-red-900 group-hover:block">
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {Array.from({ length: totalMinutes / SLOT_MINUTES }).map((_, i) => {
                      const min = (i * SLOT_MINUTES) % 60;
                      const lineColor = min === 0 ? "#C9CFD6" : min === 30 ? "#E2E6EB" : "#EEF0F3";
                      return (
                        <div key={i} className="absolute left-0 right-0"
                          style={{ top: i * SLOT_MINUTES * PX_PER_MIN, height: SLOT_MINUTES * PX_PER_MIN, borderBottom: `1px solid ${lineColor}` }} />
                      );
                    })}
                    {layoutColumnAppts(colAppts).map(({ appt: a, lane, lanes }) => {
                      const start = new Date(a.scheduled_at);
                      const minsFromStart = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
                      const dur = a.duration_minutes || 60;
                      if (minsFromStart < 0 || minsFromStart > totalMinutes) return null;
                      const h = Math.max(dur * PX_PER_MIN - 2, 26);
                      const c = apptColor(a, categories);
                      const gutterPct = 1.5;
                      const widthPct = (100 - gutterPct * 2) / lanes;
                      const leftPct = gutterPct + lane * widthPct;
                      return (
                        <ApptHover key={a.id} appointment={a} peek={peekProps}
                          className="absolute z-10 flex flex-col overflow-hidden rounded-lg text-left shadow-sm ring-1 ring-black/5 transition-shadow hover:z-20 hover:shadow-md"
                          style={{ top: minsFromStart * PX_PER_MIN, height: h, left: `${leftPct}%`, width: `${widthPct - 0.5}%`,
                            backgroundColor: c.body, borderLeft: `3px solid ${c.bar}` }}>
                          <div className="px-2 py-0.5" style={{ backgroundColor: c.head }}>
                            <p className="flex items-center gap-1 truncate text-[12px] font-semibold leading-tight text-ink">
                              <span className={`truncate ${isDeadAppt(a) ? "line-through opacity-60" : ""}`}>{a.patient_name}</span>
                              {a.is_first_visit && (
                                <span title="First visit" className="shrink-0 rounded bg-green-600 px-1 text-[9px] font-bold leading-3 text-white">N</span>
                              )}
                            </p>
                            <p className="truncate text-[10px] leading-tight text-ink/55">{fmtRange(a.scheduled_at, dur)}</p>
                          </div>
                        </ApptHover>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
